/**
 * **Tasks ▸ Preferences ▸ Board mapping** — P-294, round 11e.
 *
 * `task_board_mappings` decides which column of a board is "the status", which labels mean
 * *done*, who the assignees are and what the due date is (`specs/M10-tasks.md` §2). Until this
 * round it was **invisible**: auto-detected, never shown, and the only way to know what a board
 * had been mapped to was to read the table. On 2026-09-17 that cost the account a data incident —
 * the detector took the *first* status-type column, which on DEV-TASK BOARD, FIN, MK-MGMT-Tasks
 * and PDT is **Priority** (elsewhere Department, Type, Location, Stage, Category…), and because
 * `done_colors: [1]` is monday's default on every status column, "done" quietly meant
 * `Priority = Low` on 40 of 244 active mappings.
 *
 * The detector is fixed (`services/tasks/mapping.ts#chooseStatusColumn`, the coordinator's hot
 * fix). This panel is the other half of the answer: a person can **see** what their board is
 * mapped to and change it. Everything it writes goes through `tasks.mapping.set`, which is
 * `board.settings.update` — a board owner, or a board editor where the board allows it — and
 * which re-derives the whole board's tasks in the same transaction, so a change here re-buckets
 * My Tasks immediately instead of at the next write.
 *
 * Three rules this UI exists to make true:
 *
 *  1. **The status select offers status-type columns only.** `tasks.mapping.options` is exactly
 *     the set `tasks.mapping.set` accepts, so nothing offered can be refused.
 *  2. **Done labels are that column's own labels**, checked by **id**, never by text — monday's
 *     ids are immutable and its labels are renameable (§2.3), so the checkbox values are ids and
 *     only their captions come from `labels`. The ones monday's own `done_colors` nominates are
 *     marked, because that is where the detector starts and where the surprise came from.
 *  3. **Changing the status column clears the done labels.** Label ids are per column: keeping
 *     `[1]` while moving from Priority to Status would mark tasks done at random, which is the
 *     incident again in miniature. The server does this too (`mapping.ts`); the panel does it in
 *     the same gesture so the person sees it happen.
 */
import { useMemo } from 'react';
import type { TaskMappingOptionsView, TaskMappingView } from './types';

export interface TaskMappingPatch {
  statusColumnId?: string | null;
  doneLabels?: string[];
  assigneeColumnIds?: string[];
  dueColumnId?: string | null;
  reset?: boolean;
}

export interface TaskMappingSectionProps {
  /** The boards the person's own tasks are on — `tasks.facets().boards`. */
  boards: { id: string; name: string }[];
  /** The board being shown; `null` before one is picked or when there are no boards. */
  boardId: string | null;
  mapping: TaskMappingView | null;
  options: TaskMappingOptionsView | null;
  /** True while a board is loading or a change is being written. */
  busy: boolean;
  /** The server's own sentence when a change was refused — a permission, usually. */
  error: string | null;
  onSelectBoard(boardId: string): void;
  onChange(patch: TaskMappingPatch): void;
}

/** "Priority · Low, Medium" — what the mapping means, in one line, before anything is opened. */
export function describeMapping(
  mapping: TaskMappingView | null,
  options: TaskMappingOptionsView | null,
): string {
  if (mapping === null || options === null) return '';
  const column = options.statusColumns.find((entry) => entry.id === mapping.statusColumnId);
  if (column === undefined) return 'No status column is mapped, so every task on it stays open.';
  const labels = (column.labels ?? []).filter((label) => mapping.doneLabels.includes(label.id));
  if (labels.length === 0) {
    return `${column.title} is the status, and no label on it means done — every task on this board stays open.`;
  }
  return `${column.title} is the status; ${labels.map((label) => label.text).join(', ')} ${
    labels.length === 1 ? 'means' : 'mean'
  } done.`;
}

export function TaskMappingSection({
  boards,
  boardId,
  mapping,
  options,
  busy,
  error,
  onSelectBoard,
  onChange,
}: TaskMappingSectionProps) {
  const statusColumn = useMemo(
    () => options?.statusColumns.find((entry) => entry.id === mapping?.statusColumnId) ?? null,
    [options, mapping],
  );
  const labels = statusColumn?.labels ?? [];

  return (
    <section className="ez-tasks-mapping" data-testid="tasks-mapping">
      <h3 className="ez-tasks-settings__label">Board mapping</h3>
      <p className="ez-tasks-settings__hint">
        Which column of a board Tasks reads as its status, its assignees and its due date — and
        which labels of that column mean <em>done</em>. Changing it re-sorts My Tasks straight
        away.
      </p>

      <label className="ez-tasks-settings__row">
        <span className="ez-tasks-settings__label">Board</span>
        <select
          className="ez-tasks-settings__control"
          data-testid="tasks-mapping-board"
          value={boardId ?? ''}
          disabled={boards.length === 0}
          onChange={(event) => onSelectBoard(event.target.value)}
        >
          {boards.length === 0 ? <option value="">No boards yet</option> : null}
          {boards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name}
            </option>
          ))}
        </select>
      </label>

      {/*
        * The refusal is printed **outside** the loaded/not-loaded branch on purpose: a board that
        * could not be read has no mapping to draw, so putting the message inside the loaded half
        * is the one place it can never appear — which is exactly how a silent failure spent an
        * afternoon looking like "pick a board".
        */}
      {error === null ? null : (
        <p className="ez-tasks-mapping__error" data-testid="tasks-mapping-error" role="alert">
          {error}
        </p>
      )}

      {boardId === null || options === null || mapping === null ? (
        <p className="ez-tasks-settings__hint" data-testid="tasks-mapping-empty">
          {busy ? 'Reading the board…' : 'Pick a board to see how it is mapped.'}
        </p>
      ) : (
        <>
          <p className="ez-tasks-settings__hint" data-testid="tasks-mapping-summary">
            {describeMapping(mapping, options)}
          </p>

          <label className="ez-tasks-settings__row">
            <span className="ez-tasks-settings__label">Status column</span>
            <span className="ez-tasks-settings__hint">
              Only the board’s status columns can play this role.
            </span>
            <select
              className="ez-tasks-settings__control"
              data-testid="tasks-mapping-status"
              value={mapping.statusColumnId ?? ''}
              disabled={busy}
              onChange={(event) => {
                const next = event.target.value === '' ? null : event.target.value;
                /* Rule 3: label ids belong to one column, so the old ones do not travel. */
                onChange({ statusColumnId: next, doneLabels: [] });
              }}
            >
              <option value="">— none (every task stays open) —</option>
              {options.statusColumns.map((column) => (
                <option key={column.id} value={column.id}>
                  {column.title}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="ez-tasks-mapping__labels" data-testid="tasks-mapping-done">
            <legend className="ez-tasks-settings__label">Labels that mean done</legend>
            {labels.length === 0 ? (
              <span className="ez-tasks-settings__hint">
                {mapping.statusColumnId === null
                  ? 'No status column is mapped.'
                  : 'That column has no labels.'}
              </span>
            ) : (
              labels.map((label) => {
                const checked = mapping.doneLabels.includes(label.id);
                return (
                  <label key={label.id} className="ez-tasks-mapping__label">
                    <input
                      type="checkbox"
                      data-testid={`tasks-mapping-done-${label.id}`}
                      checked={checked}
                      disabled={busy}
                      onChange={() =>
                        onChange({
                          doneLabels: checked
                            ? mapping.doneLabels.filter((id) => id !== label.id)
                            : [...mapping.doneLabels, label.id],
                        })
                      }
                    />
                    <span
                      className="ez-tasks-mapping__swatch"
                      style={label.color === null ? undefined : { background: label.color }}
                      aria-hidden="true"
                    />
                    <span>{label.text}</span>
                    {label.isMondayDone ? (
                      <span className="ez-tasks-mapping__hint-inline">monday’s default</span>
                    ) : null}
                  </label>
                );
              })
            )}
          </fieldset>

          <label className="ez-tasks-settings__row">
            <span className="ez-tasks-settings__label">Due date column</span>
            <select
              className="ez-tasks-settings__control"
              data-testid="tasks-mapping-due"
              value={mapping.dueColumnId ?? ''}
              disabled={busy}
              onChange={(event) =>
                onChange({ dueColumnId: event.target.value === '' ? null : event.target.value })
              }
            >
              <option value="">— none —</option>
              {options.dueColumns.map((column) => (
                <option key={column.id} value={column.id}>
                  {column.title}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="ez-tasks-mapping__labels" data-testid="tasks-mapping-assignees">
            <legend className="ez-tasks-settings__label">Assignee columns</legend>
            <span className="ez-tasks-settings__hint">
              A person in any of these is assigned the task. A board with none produces no tasks.
            </span>
            {options.assigneeColumns.map((column) => {
              const checked = mapping.assigneeColumnIds.includes(column.id);
              return (
                <label key={column.id} className="ez-tasks-mapping__label">
                  <input
                    type="checkbox"
                    data-testid={`tasks-mapping-assignee-${column.id}`}
                    checked={checked}
                    disabled={busy}
                    onChange={() =>
                      onChange({
                        assigneeColumnIds: checked
                          ? mapping.assigneeColumnIds.filter((id) => id !== column.id)
                          : [...mapping.assigneeColumnIds, column.id],
                      })
                    }
                  />
                  <span>{column.title}</span>
                </label>
              );
            })}
          </fieldset>

          <div className="ez-tasks-mapping__foot">
            <span className="ez-tasks-settings__hint" data-testid="tasks-mapping-origin">
              {mapping.overridden ? 'Set by a person on this board.' : 'Detected automatically.'}
            </span>
            <button
              type="button"
              className="ez-tasks-filters__clear"
              data-testid="tasks-mapping-reset"
              disabled={busy || !mapping.overridden}
              onClick={() => onChange({ reset: true })}
            >
              Detect again
            </button>
          </div>
        </>
      )}
    </section>
  );
}
